<template>
  <div v-if="widgetConfig.metadata.state === WidgetState.SETUP_REQUIRED" class="coro-widget">
    <div class="coro-widget__content coro-widget--empty">
      <security-awareness-empty-state
        class="mt-3 mb-3"
        :daysToAllowList="widgetDataValue.data?.daysToAllowList || 0"
      >
        <template #icon>
          <v-icon class="empty-data-icon" icon="$securityAwarenessTrainingS" />
        </template>
      </security-awareness-empty-state>
    </div>
  </div>

  <div class="sat-widget" v-else-if="widgetDataValue.data">
    <coro-widget
      v-for="tile in tiles"
      :key="tile.id"
      class="sat-widget__card"
      :widget-type="widgetConfig.type"
      :data-widget-id="widgetConfig.id"
      :loading="widgetConfig.data.loading"
      :data-testid="`${tile.testIdPrefix}-card`"
    >
      <template #header>
        <div class="d-flex justify-space-between align-center w-100 h-100">
          <div class="subtitle1">{{ tile.title }}</div>
          <v-icon
            size="24"
            icon="$settings"
            class="cursor-pointer"
            @click="navigateToSettings(tile.settingsRoute)"
          />
        </div>
      </template>
      <template #content>
        <div class="coro-widget">
          <div class="d-flex flex-column align-center justify-center">
            <div
              v-if="tile.chartTotal"
              class="d-flex justify-center mt-3 coro-widget__tile__doughnut-chart"
            >
              <doughnut-chart :data="tile.chartData" :options="tile.chartOptions"> </doughnut-chart>
            </div>

            <div class="mt-3 body2">
              <a
                v-if="!securityAwarenessAccessRestricted"
                @click="navigateToReport(tile.reportRoute)"
                class="coro-link text-no-wrap"
                :data-testid="`${tile.testIdPrefix}-report-link`"
              >
                {{ tile.viewReportLabel }}
              </a>
            </div>
          </div>
          <div class="body1" :data-testid="`${tile.testIdPrefix}-list`">
            <div class="ml-5 mb-4 mt-3 mr-5">
              <div class="mb-4" v-html="tile.listTitle"></div>
              <div
                v-if="!tile.users.length"
                class="body2 text-indigo-medium"
                :data-testid="`${tile.testIdPrefix}-empty`"
              >
                {{ tile.emptyText }}
              </div>
              <div class="body2 mb-4" v-for="{ email, count } in tile.users" :key="email">
                <div class="text-break">{{ email }}</div>
                <div class="d-flex align-center">
                  <coro-progress
                    :value="getPercentage(tile.totalUsers, count)"
                    :height="4"
                    color="rgb(var(--v-theme-indigo-medium))"
                    class="mr-2"
                  >
                  </coro-progress>
                  {{ count }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </coro-widget>
  </div>

  <coro-widget
    v-else
    :widget-type="widgetConfig.type"
    :data-widget-id="widgetConfig.id"
    :loading="widgetConfig.data.loading"
  >
    <template #header>
      <widget-header :widget-type="widgetConfig.type"></widget-header>
    </template>
    <template #content>
      <div class="coro-widget">
        <widget-error-state
          :widget-type="widgetConfig.type"
          :title="$t(`dashboard.widgets.${widgetConfig.type}.error`)"
          @retry-click="$emit('fetch-data')"
        />
      </div>
    </template>
  </coro-widget>
</template>

<script lang="ts">
import { computed, defineComponent, type ComputedRef, type PropType } from "vue";
import { useWidgetContent } from "@/composables/useWidgetContent";
import { DoughnutChartColors } from "@/constants/charts";
import { useI18n } from "vue-i18n";
import DoughnutChart from "@/components/DoughnutChart.vue";
import { RouteName } from "@/constants/routes";
import { trackGtmEvent } from "@/_helpers/analytics";
import { getPercentage } from "@coronet/cui";
import CoroProgress from "@/components/CoroProgress.vue";
import SecurityAwarenessEmptyState from "@/components/SecurityAwarenessEmptyState.vue";
import { useRouter } from "vue-router";
import moment from "moment";
import { type WidgetConfiguration, WidgetState } from "@/_helpers/dashboard.ts";
import type { WidgetType } from "@/constants/dashboard.ts";
import type { SecurityAwarenessWidgetUserRecord } from "@/_store/dashboard/dashboard.module.ts";
import WidgetErrorState from "@/components/dashboard/WidgetErrorState.vue";
import WidgetHeader from "@/components/dashboard/WidgetHeader.vue";
import CoroWidget from "@/components/dashboard/CoroWidget.vue";
import { SubscriptionModule } from "@/constants/workplaces.ts";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import { RolePermissionsScope } from "@/constants/roles.ts";

export default defineComponent({
  props: {
    widgetConfig: {
      type: Object as PropType<WidgetConfiguration<WidgetType.SECURITY_AWARENESS_TRAINING>>,
      required: true,
    },
  },
  components: {
    CoroWidget,
    WidgetHeader,
    WidgetErrorState,
    SecurityAwarenessEmptyState,
    CoroProgress,
    DoughnutChart,
  },
  setup(props) {
    const i18n = useI18n();
    const router = useRouter();
    const { isAccessRestricted } = useWidgetContent();
    const widgetDataValue = computed(() => props.widgetConfig.data);

    const securityAwarenessAccessRestricted = computed(() => {
      return isAccessRestricted(
        RolePermissionsScope.PROTECTION,
        SubscriptionModule.SECURITY_AWARENESS_TRAINING
      );
    });

    // Internal Helper Functions
    const calculateTotal = (items: SecurityAwarenessWidgetUserRecord[]) => {
      return items.reduce((sum: number, curr: SecurityAwarenessWidgetUserRecord) => {
        return sum + curr.count;
      }, 0);
    };

    const sortUsers = (entry: SecurityAwarenessWidgetUserRecord[]) => {
      return entry.filter((item) => item.count).sort((itemA, itemB) => itemB.count - itemA.count);
    };

    const getPercentageForChart = (sum: number, target: number): string => {
      return `${target} (${getPercentage(sum, target)}%)`;
    };

    // Factory function for creating user computeds
    const createTopUsersComputed = (key: "topUsersFailedPhishing" | "topUsersFailedTraining") => {
      return computed(() => {
        const entries: SecurityAwarenessWidgetUserRecord[] =
          props.widgetConfig.data.data?.[key] ?? [];

        return {
          items: sortUsers(entries),
          total: calculateTotal(entries),
        };
      });
    };

    // Factory function for creating chart options
    const createChartOptions = (totalRef: ComputedRef<number>, subtitleKey: string) => {
      return computed(() => ({
        cutout: "80%",
        insideTextData: {
          title: totalRef.value,
          subtitle: i18n.t(subtitleKey),
        },
        hoverOffset: 0,
      }));
    };

    // Factory function for building chart datasets
    const buildChartDataset = (
      total: number,
      entries: Array<{ value: number; labelKey: string; color: string }>
    ) => {
      const colors = entries.map(({ color }) => color);

      return {
        hoverBackgroundColor: "red",
        hoverBorderWidth: 10,
        labels: entries.map(({ value, labelKey }) =>
          i18n.t(`dashboard.widgets.${props.widgetConfig.type}.labels.${labelKey}`, {
            n: getPercentageForChart(total, value),
          })
        ),
        datasets: [
          {
            label: "",
            backgroundColor: colors,
            hoverBackgroundColor: colors,
            data: entries.map(({ value }) => value),
          },
        ],
      };
    };

    // Internal Computed Properties

    // User Stats
    const usersFailedPhishingStats = createTopUsersComputed("topUsersFailedPhishing");
    const usersFailedTrainingStats = createTopUsersComputed("topUsersFailedTraining");

    // Chart Totals
    const usersReceivedPhishingTrainingEmails = computed(() => {
      const {
        simulationsFailed = 0,
        simulationsReported = 0,
        simulationsIgnored = 0,
      } = props.widgetConfig.data.data || {};

      return simulationsFailed + simulationsReported + simulationsIgnored;
    });

    const usersSecurityTrainingStatistics = computed(() => {
      const {
        coursesCompleted = 0,
        coursesOverdue = 0,
        coursesPending = 0,
      } = props.widgetConfig.data.data || {};

      return coursesCompleted + coursesOverdue + coursesPending;
    });

    // Chart Options (depends on totals)
    const simulationChartOptions = createChartOptions(
      usersSecurityTrainingStatistics,
      "reports.securityAwarenessReport.trainingsSent"
    );

    const phishingChartOptions = createChartOptions(
      usersReceivedPhishingTrainingEmails,
      "reports.executiveSummaryReport.securityAwarenessTraining.simulationsSent"
    );

    // Chart Data (depends on totals & helpers)
    const chartData = computed(() => {
      const {
        simulationsFailed = 0,
        simulationsReported = 0,
        simulationsIgnored = 0,
        coursesCompleted = 0,
        coursesOverdue = 0,
        coursesPending = 0,
      } = props.widgetConfig.data.data || {};

      const phishingEntries = [
        {
          value: simulationsReported,
          labelKey: "reportedPhishing",
          color: DoughnutChartColors.BLUE,
        },
        {
          value: simulationsFailed,
          labelKey: "clickedSimulations",
          color: DoughnutChartColors.ERROR,
        },
        {
          value: simulationsIgnored,
          labelKey: "noAction",
          color: DoughnutChartColors.SUCCESS,
        },
      ];

      const securityTrainingEntries = [
        {
          value: coursesCompleted,
          labelKey: "completed",
          color: DoughnutChartColors.SUCCESS,
        },
        {
          value: coursesOverdue,
          labelKey: "overdue",
          color: DoughnutChartColors.ERROR,
        },
        {
          value: coursesPending,
          labelKey: "toDo",
          color: DoughnutChartColors.GRAY,
        },
      ];

      return {
        RouteName,
        phishingEmailsChart: buildChartDataset(
          usersReceivedPhishingTrainingEmails.value,
          phishingEntries
        ),
        securityTrainingChart: buildChartDataset(
          usersSecurityTrainingStatistics.value,
          securityTrainingEntries
        ),
      };
    });

    // Final computed prop for the template (depends on all other computeds)
    const tiles = computed(() => {
      const viewReportLabel = i18n.t(`dashboard.widgets.${props.widgetConfig.type}.viewReport`);
      const noDataText = i18n.t(`dashboard.widgets.${props.widgetConfig.type}.noData`);
      const simulationsTitle = i18n.t(
        `dashboard.widgets.${props.widgetConfig.type}.trainingCourses`
      );
      const phishingTitle = i18n.t(
        `dashboard.widgets.${props.widgetConfig.type}.phishingSimulations`
      );

      return [
        {
          id: "simulations",
          testIdPrefix: `security-awareness-simulations`,
          title: simulationsTitle,
          settingsRoute: RouteName.SECURITY_TRAINING,
          reportRoute: RouteName.REPORTS_SECURITY_AWARENESS_TAB,
          viewReportLabel,
          listTitle: i18n.t(
            `dashboard.widgets.${props.widgetConfig.type}.topUsersWithOverdueTraining`
          ),
          chartData: chartData.value.securityTrainingChart,
          chartOptions: simulationChartOptions.value,
          chartTotal: usersSecurityTrainingStatistics.value,
          emptyText: noDataText,
          users: usersFailedTrainingStats.value.items,
          totalUsers: usersFailedTrainingStats.value.total,
        },
        {
          id: "phishing",
          testIdPrefix: `security-awareness-phishing`,
          title: phishingTitle,
          settingsRoute: RouteName.PHISHING_SIMULATION,
          reportRoute: RouteName.REPORTS_PHISHING_SIMULATION_SUMMARY_TAB,
          viewReportLabel,
          listTitle: i18n.t(`dashboard.widgets.${props.widgetConfig.type}.topPhishedUsers`),
          chartData: chartData.value.phishingEmailsChart,
          chartOptions: phishingChartOptions.value,
          chartTotal: usersReceivedPhishingTrainingEmails.value,
          emptyText: noDataText,
          users: usersFailedPhishingStats.value.items,
          totalUsers: usersFailedPhishingStats.value.total,
        },
      ];
    });

    // Exposed Methods
    const navigateToReport = async (routeName: RouteName) => {
      await router.push({
        name: routeName,
        query: {
          start: moment().subtract(90, "days").startOf("day").utc().valueOf(),
        },
      });
      const gtmName =
        routeName === RouteName.REPORTS_SECURITY_AWARENESS_TAB
          ? "Training courses report"
          : "Phishing simulations report";
      trackGtmEvent(
        GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET,
        `${GTMParams.SAT_WIDGET} - ${gtmName}`
      );
    };

    const navigateToSettings = async (routeName: RouteName) => {
      await router.push({
        name: routeName,
      });
      const gtmName =
        routeName === RouteName.SECURITY_TRAINING
          ? "Training courses settings"
          : "Phishing simulations settings";
      trackGtmEvent(
        GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET,
        `${GTMParams.SAT_WIDGET} - ${gtmName}`
      );
    };

    return {
      getPercentage,
      navigateToReport,
      navigateToSettings,
      securityAwarenessAccessRestricted,
      WidgetState,
      widgetDataValue,
      tiles,
    };
  },
});
</script>

<style lang="scss" scoped>
.coro-widget .coro-widget__content {
  min-height: unset !important;
}

.coro-widget .coro-widget__tile__doughnut-chart {
  margin: 0 !important;
}

.sat-widget {
  display: flex;
  gap: 16px;

  &__card {
    width: 50%;
  }
}
@media only screen and (max-width: 650px) {
  .sat-widget {
    flex-direction: column;
    &__card {
      width: 100%;
    }
  }
}

:deep(*) {
  // this font overrides are needed, because we need to make changes into SecurityAwarenessEmptyState only for widget
  // In widget there are smaller fonts than in page in which SecurityAwarenessEmptyState is used as well
  .coro-empty-state__description {
    font-size: 18px !important;
  }
  .coro-empty-state-subtitle {
    font-size: 14px !important;
  }

  .empty-state-footer-text {
    font-size: 14px !important;
  }
}
</style>
